import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { CONTENT_RELOAD_KEY } from "@/lib/contentSync";
import { deepMerge } from "@/lib/deepMerge";
import { mergeLangTranslations } from "@/lib/articles";
import { translations, contactInfo as defaultContactInfo } from "@/i18n/translations";
import { servicePages as defaultServicePages } from "@/i18n/services";
import { migratePayloadStats } from "@/lib/statsContent";
import { sanitizePayload } from "@/lib/sanitizePayload";

const SiteContentContext = createContext(null);

const emptyPayload = () => ({
  ro: {},
  en: {},
  contactInfo: {},
  servicePages: { ro: {}, en: {} },
  branding: {},
});

export function SiteContentProvider({ children }) {
  const [payload, setPayload] = useState(emptyPayload);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/site-content.php", { cache: "no-store" });
      if (!r.ok) return;
      const j = await r.json();
      setPayload(
        migratePayloadStats(
          sanitizePayload({
            ro: j.ro && typeof j.ro === "object" ? j.ro : {},
            en: j.en && typeof j.en === "object" ? j.en : {},
            contactInfo: j.contactInfo && typeof j.contactInfo === "object" ? j.contactInfo : {},
            servicePages: {
              ro: j.servicePages?.ro && typeof j.servicePages.ro === "object" ? j.servicePages.ro : {},
              en: j.servicePages?.en && typeof j.servicePages.en === "object" ? j.servicePages.en : {},
            },
            branding: j.branding && typeof j.branding === "object" && !Array.isArray(j.branding) ? j.branding : {},
          })
        )
      );
    } catch {
      /* offline or API missing — keep defaults */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    const onStorage = (e) => {
      if (e.key === CONTENT_RELOAD_KEY) load();
    };
    const onCustom = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("storage", onStorage);
    window.addEventListener("sd-content-reload", onCustom);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sd-content-reload", onCustom);
    };
  }, [load]);

  useEffect(() => {
    const prev = prevPathRef.current;
    const cur = location.pathname;
    prevPathRef.current = cur;
    if (prev.startsWith("/admin") && !cur.startsWith("/admin")) {
      load();
    }
  }, [location.pathname, load]);

  const mergedServicePages = useMemo(() => {
    const out = { ro: {}, en: {} };
    for (const lang of ["ro", "en"]) {
      const baseLang = defaultServicePages[lang] || {};
      for (const slug of Object.keys(baseLang)) {
        const ov = payload.servicePages?.[lang]?.[slug] || {};
        out[lang][slug] = deepMerge(baseLang[slug], ov);
      }
    }
    return out;
  }, [payload]);

  const value = useMemo(
    () => ({
      payload,
      reload: load,
      mergedServicePages,
      getMergedTranslations(lang) {
        const l = lang === "en" ? "en" : "ro";
        return mergeLangTranslations(translations[l], payload[l] || {});
      },
      getMergedContactInfo() {
        return deepMerge({ ...defaultContactInfo }, payload.contactInfo || {});
      },
      getMergedServicePage(lang, slug) {
        const l = lang === "en" ? "en" : "ro";
        return mergedServicePages[l]?.[slug] || null;
      },
    }),
    [payload, load, mergedServicePages]
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
  return ctx;
}
